import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { FaDownload } from "react-icons/fa";
import config from '../config';
import { uploadedFileAlert } from '../alerts/Alerts';

const CobraEmployeeCensus = forwardRef(({ caseId, renewalCase, isDisableForUsers, onError = () => { } }, ref) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState("");

    const [files, setFiles] = useState([]);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        fetchCaseFiles();
    }, []);

    const fetchCaseFiles = async () => {
        try {
            const response = await axios.get(`${config.API_URL}/CaseFiles/${caseId}`);
            setFiles(response.data || []);
        } catch (error) {
            if (error.response?.status !== 404) {
                setError('Error fetching files.');
            }
            onError(error);
        } finally {
            setLoading(false);
        }
    };

    const getFileNameFromURL = (url) => {
        try {
            const urlObject = new URL(url);
            const fileName = decodeURIComponent(urlObject.pathname.split('/').pop());
            const fileNameWithoutId = fileName.split('_').slice(0, -1).join('_');
            return fileNameWithoutId;
        } catch (error) {
            console.error('Invalid URL:', url);
            return '';
        }
    };
    
    // Example usage
    
    

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setSelectedFileName(file.name);
        }
    };

    const uploadCobraBenefitsFile = async (fileCategory) => {
        let errors = { ...validationErrors };
        if (!selectedFile) {
            setUploadStatus("No file selected.");
            return;
        }
    
        const formData = new FormData();
        formData.append("file", selectedFile);
    
        try {
            
            const blobResponse = await axios.post(`${config.API_URL}/BlobUploads/upload`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
    
            const fileUrl = blobResponse.data.fileUrl;
            setUploadStatus(`File uploaded successfully: ${fileUrl}`);
    
           
            const caseFile = {
                fileURL: fileUrl,
                plan: "employeecensus",
                fileCategory: `${fileCategory}`,
                caseId: caseId
            };
    
            await axios.post(`${config.API_URL}/CaseFiles`, caseFile);
            setUploadStatus(`File and metadata saved successfully: ${fileUrl}`);
            
            // Remove validation error after successful upload
            delete errors['employeecensus'];
            setValidationErrors(errors);
            uploadedFileAlert();
            setSelectedFile(null);
            setSelectedFileName('');
            fetchCaseFiles();
    
        } catch (error) {
            console.error("Error during file upload or case file save:", error);
            setUploadStatus("File upload or case file save failed.");
        }
    };
    

    const validateAll = () => {
        
        let errors = {};

        const hasFiles = files.filter(file => file.plan === 'employeecensus' && file.fileCategory === 'employeecensus' && file.caseId === caseId).length > 0;

        if (!hasFiles) {
            errors.employeecensus = 'Employee census file must be uploaded.';
        }

        setValidationErrors(errors);

        // Return true if no errors
        return Object.keys(errors).length === 0;
    };

    useImperativeHandle(ref, () => ({
        validateAll,
        
    }));

    const handleDeleteFile = async (fileId) => {
        try {
            await axios.delete(`${config.API_URL}/CaseFiles/${fileId}`);
            fetchCaseFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            onError(error);
        }
    };

    //const isDisabled = renewalCase?.cobraSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled = isDisableForUsers;

    return (
        <div className="col-lg-12 mb-3">
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title">Required Documents</h5>

                    <div className="d-flex justify-content-center mb-2">
                        <em>Please upload your most recent Employee Census. The information is used by our finance team to identify the number of benefit eligible employees that may experience a qualifying event. We collect this information on an annual basis during renewal to ensure our records are up to date.</em>
                    </div>

                    <div className="col-lg-12">
                        <div className="table-responsive">
                            <table className="styled-table" cellSpacing="0" width="100%">
                                <thead>
                                    <tr>
                                        <th className="fileUploads-table-row">File Category</th>
                                        <th colSpan="2" className="fileUploads-table-row">File Uploads</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th className="vertical-th fileUploadsCobrabenefits" scope="row">
                                            <span className="browse-text pt-1">Employee Census</span>
                                        </th>
                                        <td className="fileuploads-table-row">
                                            <div className="upload-section-funding">
                                                <div className="d-flex justify-content-around mb-1">
                                                    <input
                                                        type="file"
                                                        accept=".pdf, .xlsx, .xls, .csv"
                                                        onChange={handleFileChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <label className="file-input-label pt-2">
                                                        {selectedFileName ? selectedFileName : <>Browse files here.<br />Acceptable file formats include .pdf, .xlsx, .xls, and .csv</>}
                                                    </label>
                                                    {selectedFile && (
                                                        <button
                                                        className="btn btn-success emp-upload-button btn-sm mt-2"
                                                        onClick={() => uploadCobraBenefitsFile("employeecensus")}
                                                        disabled={isDisabled}
                                                    >
                                                        Click to Upload
                                                    </button>
                                                    )}
                                                    
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {validationErrors.employeecensus && (
                            <div className="invalid-feedback d-block">
                                {validationErrors.employeecensus}
                            </div>
                        )}

                        <div className="col-lg-6">
                            <div className="table-responsive">
                                {files.filter(file => file.fileCategory === "employeecensus" && file.caseId === caseId).length > 0 && (
                                    <table className="table" cellSpacing="0" width="100%">
                                        <thead>
                                            <tr>
                                                <th className="fileUploads-table-row">File Name</th>
                                                <th className="fileUploads-table-row">File Category</th>
                                                <th className="fileUploads-table-row d-flex justify-content-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {files.filter(file => file.fileCategory === "employeecensus" && file.caseId === caseId).map((file, idx) => (
                                                <tr key={idx}>
                                                    <th className="vertical-th fileUploadsCobrabenefits" scope="row">
                                                        <span className="FileURLElements browse-text">{getFileNameFromURL(file.fileURL)}</span>
                                                    </th>
                                                    <td>{file.fileCategory}</td>
                                                    <td className="d-flex justify-content-between">
                                                        <a href={file.fileURL} className="" download="" style={{ textDecoration: 'none' }}>
                                                            <FaDownload style={{ fontSize: '20px' }} />
                                                        </a>
                                                        <button type="button" className="deleteFile" disabled={isDisabled} onClick={() => handleDeleteFile(file.id)}>
                                                            <span className="fa fa-trash-alt me-1"></span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CobraEmployeeCensus;
